// Items built, the Quick check and the Engineering check (docs/job-view-brief.md, section 2; the drawings are
// docs/screens/job-view-screens-round4-settled-set.html frames f3a, f3b, f5a, f5b and round 5 frames f1a, f1b).
// One layout for all three: the drawing at the left (the View with its marks, or the Fronts view), the tree by wall at
// the right, and the open item's panel in the tree's place with Back to list. Items built adds the sheet tools; the
// walk adds the bar above (the build, the walls with their counts, Drawing / Fronts, Only what moved, the progress),
// the check circles, the next flag and the check button; the Quick check shows each wall's full item table instead of
// the tree. The pieces are under ./walk/.

import { useEffect, useMemo, useState } from 'react';
import { jobFindings, placement, type ResolvedItem } from '@zerogap/engine';
import { useRows } from '../../lib/api';
import { useJobCheck } from '../../lib/check';
import { markStepDone } from '../../lib/steps';
import { useSession } from '../../lib/session';
import { MultiEdit } from './ItemTab';
import type { JobCtx } from './Workspace';
import { Picture, PlacingTray, RoomNote, ViewHead, sheetTray } from './walk/Drawing';
import { FrontsView } from './walk/Fronts';
import { ItemPanel } from './walk/ItemPanel';
import { JobStopPanel } from './walk/JobStop';
import { FlagForm, WallFlags, raiseFlag, useFlags, useSeats } from './walk/Flags';
import { useBuildPricing, useWalkModel, wallShort } from './walk/model';
import { QuickTable } from './walk/Quick';
import { Tree } from './walk/Tree';
import '../../styles/jobview-walk.css';

type Mode = 'items' | 'quick' | 'walk';

export function Walk({ ctx, mode, onAdd, onGrid }: { ctx: JobCtx; mode: Mode; onAdd: () => void; onGrid: () => void }) {
  const s = useSession();
  const model = useWalkModel(ctx);
  const check = useJobCheck(ctx);
  const users = useRows('user');
  const jobSteps = useRows('job_step', { eq: { job_id: ctx.jobId } });
  const buildPricing = useBuildPricing(ctx);
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  // placing is on from the address too (the Views table's Place marks, the item panel's Place on…): the flag is read once and taken off the address
  const [placing, setPlacing] = useState(() => ctx.place);
  const [linkTarget, setLinkTarget] = useState('');
  useEffect(() => {
    if (!ctx.place) return;
    setPlacing(true);
    ctx.go({ place: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.place]);
  const [picking, setPicking] = useState<string[] | null>(null);
  const [onlyMoved, setOnlyMoved] = useState(false);
  const [multi, setMulti] = useState(false);
  const walk = mode !== 'items';
  const canEdit = s.canEdit('screen.item') && !ctx.locked;
  // the flags on the check (the Quick check pack, 2026-10-24) and the seats, for who each waits on
  const flags = useFlags(ctx.jobId);
  const seats = useSeats();
  const [flaggingWall, setFlaggingWall] = useState(false);

  // the open item (or a job stop: the job route, the Job BOM) and the wall shown: the address's, else the item's, else the first View
  const item = ctx.itemId ? model.res.items.find((i) => i.id === ctx.itemId) ?? null : null;
  const jobStop = ctx.itemId && !item ? model.stops.find((x) => !x.item && x.key === ctx.itemId) ?? null : null;
  const stop = item ? model.stopOf(item.id) : jobStop;
  const views = useMemo(() => [...ctx.data.views].sort((a, b) => Number(a.sort ?? 0) - Number(b.sort ?? 0)), [ctx.data.views]);
  const known = (id: string | null) => !!id && (id === 'room' || views.some((v) => v.id === id));
  const wall = known(ctx.viewId) ? ctx.viewId! : stop ? stop.view_id ?? 'room' : views[0]?.id ?? 'room';
  const view = views.find((v) => v.id === wall) ?? null;

  // the walk's progress: every stop in walk order, the first not checked is next
  const next = model.stops.find((x) => model.stateOf(x) !== 'checked') ?? null;
  const done = model.stops.filter((x) => model.stateOf(x) === 'checked').length;
  const stepKey = ctx.tab === 'quick_check' ? 'quick_check' : ctx.tab === 'engineering_review' ? 'engineering_review' : 'engineering_check';
  const stepRow = ctx.steps.find((x) => x.key === stepKey);
  const stepDone = !!stepRow && jobSteps.rows.some((js) => js.workflow_step_id === stepRow.id && js.actual_date);

  const pick = (it: ResolvedItem | { id: string }) => {
    const st = 'number' in it ? model.stopOf(it.id) : model.stops.find((x) => x.key === it.id) ?? null;
    ctx.go({ item: it.id, view: st ? st.view_id ?? 'room' : null, lit: null, back: null });
  };
  const pickId = (id: string) => {
    const it = model.res.items.find((i) => i.id === id);
    pick(it ?? { id });
  };
  const back = () => ctx.go({ item: null, lit: null, back: null });
  // on a phone or tablet the drawing sits above the item: an item opened scrolls into view so the tap shows what it opened (Iris, 2026-10-22)
  useEffect(() => {
    if (!ctx.itemId || s.device === 'desk' || ctx.lit) return;
    const t = window.setTimeout(() => document.querySelector('.jv .work > .panel')?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 60);
    return () => window.clearTimeout(t);
  }, [ctx.itemId, s.device, ctx.lit]);
  /** After a check: the next stop on the same wall not yet checked, else the next anywhere. */
  const afterCheck = (id: string) => {
    const at = model.stops.findIndex((x) => x.key === id);
    const here = model.stops[at];
    const open = (x: (typeof model.stops)[number]) => x.key !== id && model.stateOf(x) !== 'checked';
    const sameWall = model.stops.slice(at + 1).find((x) => x.view_id === here?.view_id && open(x)) ?? model.stops.find((x) => x.view_id === here?.view_id && open(x));
    const nx = sameWall ?? model.stops.slice(at + 1).find(open) ?? model.stops.find(open);
    if (nx) pick(nx.item ?? { id: nx.key });
    else back();
  };
  const onWall = (id: string) => {
    if (walk && (item || jobStop)) {
      const first = model.stops.find((x) => (x.view_id ?? 'room') === id && model.stateOf(x) !== 'checked') ?? model.stops.find((x) => (x.view_id ?? 'room') === id);
      ctx.go({ view: id, item: first ? first.item?.id ?? first.key : null, lit: null, back: null });
      return;
    }
    ctx.go({ view: id, item: null, lit: null, back: null });
  };
  const tray = sheetTray(ctx, view, model.res.items);
  const toPlace = new Set(tray.toPlace.map((i) => i.id));
  // what is placed on no View ("Not placed yet"), by number; a Whole-room item by design is not listed
  const placed = placement(model.res, views as any[], ctx.data.marks as any[]);
  /** Turn placing on for one item on the sheet in view (the "Not placed yet" group's Place on this sheet, the tray's Place first). */
  const placeItem = (i: ResolvedItem, viewId?: string | null) => {
    setPlacing(true);
    ctx.go({ item: i.id, view: viewId ?? view?.id ?? null, show: null });
  };
  const pickedItems = (picking ?? []).map((id) => model.res.items.find((i) => i.id === id)).filter((i): i is ResolvedItem => !!i);
  const wallButtons = views.map((v) => {
    const list = model.stops.filter((x) => x.view_id === v.id);
    return { id: v.id as string, name: wallShort(v.name), full: v.name as string, d: list.filter((x) => model.stateOf(x) === 'checked').length, t: list.length };
  });
  const roomList = model.stops.filter((x) => !x.view_id);
  const fronts = ctx.showFronts;
  // "+ Flag on Wall 1" beside the wall buttons; "+ Flag on the room" under Whole room (Q5): a flag in Spencer's own words
  const wallLabel = wall === 'room' ? 'the room' : wallShort(view?.name);
  const flagWall = async (text: string, note: string) => {
    await raiseFlag(ctx, { target: wall === 'room' ? 'room' : 'wall', view: wall === 'room' ? null : wall, text, note });
    setFlaggingWall(false);
  };
  const switcher = (
    <div className="seg sm" role="group" aria-label="Drawing or Fronts">
      <button type="button" className={fronts ? '' : 'on'} onClick={() => ctx.go({ show: null })}>
        Drawing
      </button>
      <button type="button" className={fronts ? 'on' : ''} onClick={() => ctx.go({ show: 'fronts' })}>
        Fronts
      </button>
    </div>
  );
  const scope = useRows('scope_row', { eq: { opportunity_id: ctx.data.job?.opportunity_id }, enabled: !!ctx.data.job?.opportunity_id });
  const tally = {
    item: scope.rows.filter((x) => x.outcome === 'item').length,
    dismissed: scope.rows.filter((x) => x.outcome === 'dismissed').length,
    job: scope.rows.filter((x) => x.outcome === 'job').length,
    pending: scope.rows.filter((x) => x.outcome === 'pending' || !x.outcome).length,
  };
  const wide = jobFindings(check.findings);

  const left = (
    <div className="left">
      {!walk && (
        <>
          <ViewHead
            ctx={ctx}
            view={view}
            walls={views}
            onWall={onWall}
            placing={placing}
            setPlacing={setPlacing}
            onAdd={onAdd}
            onGrid={onGrid}
            selecting={picking !== null}
            setSelecting={(b) => {
              setPicking(b ? [] : null);
              setMulti(false);
            }}
            showFronts={fronts}
          />
          {!fronts && <PlacingTray ctx={ctx} model={model} view={view} placing={placing} setPlacing={setPlacing} onPlace={(i) => placeItem(i)} linkTarget={linkTarget} setLinkTarget={setLinkTarget} />}
        </>
      )}
      {fronts ? (
        <FrontsView ctx={ctx} model={model} wallId={wall} selectedId={ctx.itemId} onPick={pickId} users={users.rows} />
      ) : view ? (
        <>
          <Picture ctx={ctx} model={model} view={view} selectedId={ctx.itemId} placing={placing && !walk} walk={walk} nextId={walk && !ctx.itemId ? next?.item?.id ?? null : null} onPick={pickId} linkTarget={linkTarget} />
          <div className="dnote">
            {view.name}
            {!walk && ` · View ${views.findIndex((v) => v.id === view.id) + 1} of ${views.length} · ${placed.on_view[view.id as string] ?? 0} of ${placed.wanted.length} items on this sheet`}
            {walk ? ' · Hollow with a grey number: this build does not carry it.' : ' · Tap a mark to open its item.'}
          </div>
        </>
      ) : (
        <>
          {views.length === 0 && wall !== 'room' ? (
            <div className="roomnote">
              <b>No View yet</b>
              <span>Upload a mockup, a line drawing or a photo under Files and choose "Use as view"; then place the items' marks on it.</span>
            </div>
          ) : (
            <RoomNote />
          )}
          {walk && (
            <div className="rtally">
              <div>
                <b>{tally.item}</b>
                <span>scope rows have an item</span>
              </div>
              <div>
                <b>{tally.dismissed}</b>
                <span>dismissed with a reason</span>
              </div>
              <div>
                <b>{tally.job}</b>
                <span>are job standards</span>
              </div>
              {tally.pending > 0 && (
                <div className="fx">
                  <b>{tally.pending}</b>
                  <span>still waiting on an outcome</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  let right: React.ReactNode;
  if (picking !== null && multi && pickedItems.length > 0) {
    right = (
      <div className="walkpanel multi">
        <div className="back">
          <button type="button" className="btn read" onClick={() => setMulti(false)}>
            ‹ Back to picking
          </button>
          <span className="where">{pickedItems.map((i) => i.number).join(', ')} · a change writes to each</span>
        </div>
        <div className="sect">
          <MultiEdit ctx={ctx} items={pickedItems} />
        </div>
      </div>
    );
  } else if (item) {
    right = <ItemPanel ctx={ctx} model={model} mode={mode} item={item} findings={check.findings} flags={flags} seats={seats} users={users.rows} buildPricing={buildPricing} onPick={pickId} onBack={back} onChecked={afterCheck} views={views} unplaced={placed.unplaced} onPlace={(viewId) => placeItem(item, viewId)} />;
  } else if (jobStop) {
    right = <JobStopPanel ctx={ctx} model={model} mode={mode} stop={jobStop} res={model.res} users={users.rows} onBack={back} onChecked={afterCheck} />;
  } else if (mode === 'quick') {
    right = <QuickTable ctx={ctx} model={model} wall={wall} findings={check.findings} flags={flags} onPick={pick} users={users.rows} seats={seats} />;
  } else {
    right = (
      <Tree
        ctx={ctx}
        model={model}
        mode={mode}
        wall={wall}
        selectedId={ctx.itemId}
        open={open}
        onToggle={(id) => {
          const n = new Set(open);
          if (n.has(id)) n.delete(id);
          else n.add(id);
          setOpen(n);
        }}
        onPick={pick}
        findings={check.findings}
        flags={flags}
        users={users.rows}
        nextId={walk ? next?.item?.id ?? next?.key ?? null : null}
        onlyMoved={walk && onlyMoved}
        toPlace={toPlace}
        notPlaced={placed.unplaced}
        placingId={placing && !walk ? ctx.itemId : null}
        onPlace={!walk && canEdit && view ? (i) => placeItem(i) : undefined}
        picking={picking}
        onEditPicked={() => setMulti(true)}
        onPickToggle={(id) => setPicking((p) => (p ?? []).includes(id) ? (p ?? []).filter((x) => x !== id) : [...(p ?? []), id])}
      />
    );
  }

  return (
    <div className={`walk ${mode}`}>
      {walk && (
        <div className="walkbar">
          {ctx.data.builds.length > 0 && (
            <>
              <span className="lbl">Build</span>
              <div className="seg" role="group" aria-label="Build">
                {[...ctx.data.builds].sort((a, b) => Number(!!b.is_base) - Number(!!a.is_base)).map((b) => (
                  <button key={b.id} type="button" className={ctx.build?.id === b.id ? 'on' : ''} title={`${b.name}${b.is_base ? ' · the base' : ''}: named on the Scope table`} onClick={() => ctx.go({ build: b.id as string })}>
                    {b.name}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="walls" role="group" aria-label="Walls">
            {wallButtons.map((w) => (
              <button key={w.id} type="button" className={wall === w.id ? 'on' : ''} title={w.full} onClick={() => onWall(w.id)}>
                {w.name}
                <span className="ct">
                  {w.d} of {w.t}
                </span>
              </button>
            ))}
            {roomList.length > 0 && (
              <button type="button" className={wall === 'room' ? 'on' : ''} onClick={() => onWall('room')}>
                Whole room
                <span className="ct">
                  {roomList.filter((x) => model.stateOf(x) === 'checked').length} of {roomList.length}
                </span>
              </button>
            )}
          </div>
          {canEdit && !flaggingWall && (
            <button type="button" className="btn" title={`Flag something on ${wallLabel} for the engineer, in your own words`} onClick={() => setFlaggingWall(true)}>
              + Flag on {wallLabel}
            </button>
          )}
          {views.length > 0 && switcher}
          {mode === 'walk' && (
            <button type="button" className={`sw sm${onlyMoved ? ' on' : ''}`} aria-pressed={onlyMoved} title="A recheck: only the items an edit cleared since they were checked" onClick={() => setOnlyMoved(!onlyMoved)}>
              <i />
              <span>Only what moved</span>
            </button>
          )}
          <div className="prog">
            <span className="bar">
              <i style={{ width: `${model.stops.length ? Math.round((done / model.stops.length) * 100) : 0}%` }} />
            </span>
            <span>
              {done} of {model.stops.length} checked
              {next ? ` · next ${next.item?.number ?? (next.kind === 'job_route' ? 'the job route' : 'the Job BOM')}` : ''}
            </span>
            {!next && model.stops.length > 0 && canEdit && stepRow && (
              stepDone ? (
                <span className="tag ok">{stepRow.name} done</span>
              ) : (
                <button type="button" className="btn pri" onClick={() => void markStepDone(ctx.data.job!, ctx.steps, stepKey)}>
                  Finish the {stepRow.name}
                </button>
              )
            )}
          </div>
          {flaggingWall && <FlagForm what={`on ${wallLabel}`} onFlag={flagWall} onCancel={() => setFlaggingWall(false)} />}
        </div>
      )}
      {walk && <WallFlags ctx={ctx} flags={flags} wallId={wall} users={users.rows} seats={seats} canEdit={canEdit} />}
      {walk && wide.length > 0 && (
        <details className="jobfinds">
          <summary>
            <span className="lbl">The job check · {wide.length}</span> <span className="jf">{wide[0]!.what}</span>
          </summary>
          {wide.slice(1).map((f, i) => (
            <span key={i} className="jf">
              {f.what}
            </span>
          ))}
        </details>
      )}
      <div className="work">
        {left}
        <div className="panel">{right}</div>
      </div>
    </div>
  );
}
